import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { LegalDisclaimer } from "@/context/DisclaimerContext";
import { Logo } from "@/components/Logo";
import { HelpHint } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, MapPin, ShieldCheck, Lock, CheckCircle2, X, ChevronLeft, ChevronRight,
  Camera, AlertTriangle, RotateCw, Info,
} from "lucide-react";

const NAME_RE = /^[A-Za-z][A-Za-z .'-]{0,98}[A-Za-z.]$/;
const MAX_PHOTOS = 20;
const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
const fmtDT = (d) => (d ? new Date(d).toLocaleString() : "");

function Img({ src, alt, onClick }) {
  const [err, setErr] = useState(false);
  if (err) return <div className="aspect-video grid place-items-center rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] text-xs text-[#94A3B8]">Photo unavailable</div>;
  return <img src={`${API}${src}`} alt={alt} onError={() => setErr(true)} onClick={onClick}
    className="aspect-video w-full object-cover rounded-lg border border-[#E2E8F0] cursor-zoom-in" />;
}

export default function LandlordBaselineView() {
  const { slug } = useParams();
  const [state, setState] = useState("loading"); // loading | ok | notfound | unavailable
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  // signing
  const [name, setName] = useState("");
  const [signing, setSigning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signError, setSignError] = useState("");

  // notes
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // counter-photos
  const [photos, setPhotos] = useState([]);
  const [uploads, setUploads] = useState([]); // {key, name, progress, error}
  const fileRef = useRef(null);

  useEffect(() => {
    document.title = "Move-In Condition Baseline";
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    axios.get(`${API}/baseline/${slug}`)
      .then(({ data }) => {
        setData(data);
        setNotes(data.landlord_notes || "");
        setPhotos(data.counter_photos || []);
        setState(data.available ? "ok" : "unavailable");
      })
      .catch(() => setState("notfound"));
    return () => { document.head.removeChild(m); };
  }, [slug]);

  const openLb = (images, idx) => setLightbox({ images, idx });
  const move = (d) => setLightbox((lb) => ({ ...lb, idx: (lb.idx + d + lb.images.length) % lb.images.length }));

  const nameValid = name.trim().length >= 2 && name.trim().length <= 100 && NAME_RE.test(name.trim());

  const doSign = async () => {
    setConfirmOpen(false);
    setSigning(true);
    setSignError("");
    try {
      const { data: res } = await axios.post(`${API}/baseline/${slug}/sign`, {
        legal_name: name.trim(), notes: notes.trim() || null,
      });
      setData((d) => ({ ...d, ...res, landlord_signed: true }));
      setNotesDirty(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) setData((d) => ({ ...d, landlord_signed: true, landlord_legal_name: name.trim(), signature_timestamp: new Date().toISOString() }));
      else if (status === 422) setSignError(e?.response?.data?.detail || "Please enter a valid full legal name.");
      else setSignError("Something went wrong. Please try again.");
    } finally {
      setSigning(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.post(`${API}/baseline/${slug}/notes`, { notes: notes.trim() || null });
      setNotesDirty(false);
    } catch (e) { console.error("save baseline notes failed", e); /* keep dirty so user can retry */ }
    finally { setSavingNotes(false); }
  };

  const uploadFile = async (file, key) => {
    const form = new FormData();
    form.append("file", file);
    try {
      const { data: res } = await axios.post(
        `${API}/baseline/${slug}/counter-photos`, form,
        { onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploads((u) => u.map((x) => x.key === key ? { ...x, progress: pct } : x));
          } }
      );
      setPhotos((p) => [...p, res]);
      setUploads((u) => u.filter((x) => x.key !== key));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      setUploads((u) => u.map((x) => x.key === key ? { ...x, error: msg, progress: 0, file } : x));
    }
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const room = MAX_PHOTOS - photos.length - uploads.length;
    if (files.length > room) {
      files.splice(room);
    }
    files.forEach((file) => {
      const key = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setUploads((u) => [...u, { key, name: file.name, progress: 0, error: "" }]);
      uploadFile(file, key);
    });
  };

  const retry = (up) => {
    setUploads((u) => u.map((x) => x.key === up.key ? { ...x, error: "", progress: 0 } : x));
    uploadFile(up.file, up.key);
  };

  if (state === "loading") return <div className="min-h-screen bg-[#EEF2F6] p-6"><div className="max-w-3xl mx-auto space-y-4"><div className="h-10 w-48 bg-[#E2E8F0] rounded animate-pulse" /><div className="h-64 bg-[#E2E8F0] rounded-2xl animate-pulse" /></div></div>;

  if (state === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="baseline-notfound">
      <h1 className="font-heading text-2xl font-bold text-navy">This report link is invalid or no longer available.</h1>
    </div>
  );

  if (state === "unavailable") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="baseline-unavailable">
      <div>
        <Info size={28} className="text-brand mx-auto" />
        <h1 className="mt-3 font-heading text-2xl font-bold text-navy">This report is not yet available.</h1>
        <p className="mt-1 text-sm text-[#475569]">The tenant hasn't finished documenting the move-in condition.</p>
      </div>
    </div>
  );

  const p = data.property;
  const signed = data.landlord_signed;
  const remaining = MAX_PHOTOS - photos.length - uploads.length;

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Logo />
          <div className="mt-3 flex items-center gap-1">
            <h1 className="font-heading text-2xl font-bold text-navy">Move-In Condition Baseline</h1>
            <HelpHint topic="baseline" />
          </div>
          <p className="text-sm text-[#475569] flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {p.address}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6" data-testid="landlord-baseline-view">
        {/* Explainer */}
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="baseline-explainer">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={20} className="text-brand mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-[#334155]">Your tenant has documented the move-in condition of this property. Review the photos below. By acknowledging, you confirm this record reflects the property's condition at move-in.</p>
              <LegalDisclaimer keyName="baseline_agreement" className="mt-2 text-xs text-[#94A3B8]" />
            </div>
          </div>
        </div>

        {/* Move-in photos */}
        <div className="mt-6">
          <h2 className="font-heading text-lg font-semibold text-navy">Move-in photos</h2>
          {data.rooms.map((r, i) => {
            const imgs = r.photos.map((ph) => ({ src: ph.image_path, label: `${r.name} · ${fmt(ph.created_at)}` }));
            return (
              <div key={i} className="mt-4" data-testid={`baseline-room-${i}`}>
                <p className="text-sm font-medium text-navy">{r.name}</p>
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {imgs.map((im, j) => (
                    <div key={j}><Img src={im.src} alt={im.label} onClick={() => openLb(imgs, j)} /><p className="mt-1 text-[11px] text-[#94A3B8]">{fmt(r.photos[j].created_at)}</p></div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Notes */}
        <div className="mt-8" data-testid="baseline-notes-section">
          <label className="text-sm font-medium text-navy" htmlFor="landlord-notes">Add notes or disagreements (optional)</label>
          <Textarea id="landlord-notes" data-testid="baseline-notes-input" value={notes} maxLength={2000}
            onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
            placeholder="e.g. I noticed a scuff on the living room wall that isn't shown here."
            className="mt-2 min-h-[96px] rounded-xl bg-white" />
          <div className="mt-1 flex items-center justify-between">
            <span className="text-[11px] text-[#94A3B8]">{notes.length}/2000</span>
            {notesDirty && (
              <Button data-testid="save-notes-btn" size="sm" variant="outline" disabled={savingNotes} onClick={saveNotes} className="rounded-lg h-8">
                {savingNotes ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Save notes
              </Button>
            )}
          </div>
        </div>

        {/* Counter-photos */}
        <div className="mt-8" data-testid="baseline-counterphotos-section">
          <p className="text-sm font-medium text-navy">Add your own photos (optional)</p>
          <p className="text-[11px] text-[#94A3B8]">{photos.length + uploads.length}/{MAX_PHOTOS} added</p>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple hidden onChange={onPick} data-testid="counterphoto-input" />
          <Button data-testid="add-counterphoto-btn" variant="outline" disabled={remaining <= 0}
            onClick={() => fileRef.current?.click()} className="mt-2 rounded-xl">
            <Camera size={16} className="mr-2" /> {remaining <= 0 ? "Limit reached (20)" : "Add photos"}
          </Button>

          {(photos.length > 0 || uploads.length > 0) && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
              {photos.map((c) => (
                <div key={c.id} data-testid={`counterphoto-${c.id}`}>
                  <img src={`${API}${c.image_path}`} alt={c.caption || "counter photo"} className="aspect-square w-full object-cover rounded-lg border border-[#E2E8F0]" />
                  {c.caption && <p className="mt-0.5 text-[10px] text-[#94A3B8] truncate">{c.caption}</p>}
                </div>
              ))}
              {uploads.map((u) => (
                <div key={u.key} data-testid="counterphoto-uploading" className="aspect-square rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] grid place-items-center p-2 text-center">
                  {u.error ? (
                    <div>
                      <AlertTriangle size={16} className="text-[#B91C1C] mx-auto" />
                      <p className="text-[10px] text-[#B91C1C] mt-1 line-clamp-2">{u.error}</p>
                      <button data-testid="retry-upload-btn" onClick={() => retry(u)} className="mt-1 text-[10px] text-brand inline-flex items-center gap-1"><RotateCw size={11} /> Retry</button>
                    </div>
                  ) : (
                    <div className="w-full">
                      <Loader2 size={16} className="text-brand mx-auto animate-spin" />
                      <div className="mt-2 h-1 w-full rounded bg-[#E2E8F0]"><div className="h-1 rounded bg-brand transition-all" style={{ width: `${u.progress}%` }} /></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Signature block */}
        {signed ? (
          <div className="mt-8 rounded-2xl border border-[#A7F3D0] bg-[#ECFDF5] p-5" data-testid="baseline-signed-card">
            <div className="flex items-start gap-3">
              <Lock size={22} className="text-[#177245] mt-0.5" />
              <div>
                <p className="font-heading font-semibold text-[#177245]">Baseline acknowledged</p>
                <p className="mt-1 text-sm text-[#334155]">Acknowledged by <b>{data.landlord_legal_name}</b> on {fmtDT(data.signature_timestamp)}. The tenant has been notified.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 rounded-2xl border border-[#E2E8F0] bg-white p-5" data-testid="signature-block">
            <label className="text-sm font-medium text-navy" htmlFor="legal-name">Type your full legal name to acknowledge</label>
            <Input id="legal-name" data-testid="legal-name-input" value={name} onChange={(e) => { setName(e.target.value); setSignError(""); }}
              placeholder="e.g. Jane A. Smith" className="mt-2 rounded-xl" />
            {signError && <p className="mt-1 text-xs text-[#B91C1C]" data-testid="sign-error">{signError}</p>}
            <Button data-testid="acknowledge-btn" disabled={!nameValid || signing} onClick={() => setConfirmOpen(true)}
              className="mt-3 w-full h-12 rounded-xl bg-navy hover:bg-navy-hover text-white text-base font-semibold">
              {signing ? <><Loader2 size={18} className="mr-2 animate-spin" /> Recording acknowledgment…</> : "Acknowledge Baseline Condition"}
            </Button>
            {nameValid && <p className="mt-2 text-xs text-[#94A3B8] text-center">Signing as: {name.trim()} · {new Date().toLocaleDateString()}</p>}
          </div>
        )}

        <footer className="mt-10 py-6 text-center">
          <a href="/" className="text-sm text-[#94A3B8] hover:text-brand">Prepared with TenantSeal</a>
        </footer>
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Acknowledge this baseline?</AlertDialogTitle>
            <AlertDialogDescription>
              You're signing as <b>{name.trim()}</b>. The move-in baseline will be locked and the tenant notified. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-acknowledge-btn" onClick={doSign} className="bg-navy hover:bg-navy-hover">Acknowledge</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4" data-testid="lightbox" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white" onClick={() => setLightbox(null)}><X size={28} /></button>
          <button className="absolute left-3 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); move(-1); }}><ChevronLeft size={32} /></button>
          <img src={`${API}${lightbox.images[lightbox.idx].src}`} alt="" className="max-h-[85vh] max-w-full rounded-lg" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-3 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); move(1); }}><ChevronRight size={32} /></button>
          <p className="absolute bottom-4 text-white/80 text-sm">{lightbox.images[lightbox.idx].label}</p>
        </div>
      )}
    </div>
  );
}
